import { useState, useEffect, useRef } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const BASE_URL      = "https://senutour.com/api";
const CLOUD_NAME    = "dhs2cu6vv";
const UPLOAD_PRESET = "senutour_unsigned";

// Catégorie de service fixe pour ce dashboard.
const CATEGORIE_SERVICE = "Location voiture";

const getToken = () => localStorage.getItem("token");
const getUser  = () => JSON.parse(localStorage.getItem("user") || "{}");

const ONGLETS = [
  { id: "dashboard",  label: "Vue générale" },
  { id: "vehicules",  label: "Mes véhicules" },
  { id: "locations",  label: "Locations en cours" },
  { id: "historique", label: "Historique & Revenus" },
  { id: "avis",       label: "Notes & Avis" },
  { id: "contact",    label: "Contact & Parking" },
];

const TYPES_VEHICULE  = ["Berline", "SUV", "Minibus", "Pick-up", "Utilitaire", "4x4", "Citadine"];
const VILLES          = ["Dakar", "Diamniadio", "Saly", "Saint-Louis", "Ziguinchor", "Thiès"];
const EQUIPEMENTS_LISTE = ["Climatisation", "GPS", "Bluetooth", "Sièges enfant", "Chauffeur inclus", "Carburant inclus", "Coffre spacieux", "4 portes"];

const VEHICULE_VIDE = {
  nom: "", typeVehicule: "Berline", marque: "", modele: "", annee: "",
  villeDepart: "Dakar", prix: "", prixJournee: "", capacite: "",
  telephone: "", whatsapp: "", nomProprietaire: "", description: "",
  equipements: [], disponible: true,
};

const formatPrix = (p) => p ? Number(p).toLocaleString("fr-FR") + " F" : "—";

export default function LoueurDashboard() {
  const user     = getUser();
  const fileRef  = useRef();

  const [onglet, setOnglet]           = useState("dashboard");
  const [vehicules, setVehicules]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filtreType, setFiltreType]   = useState("tous");
  const [filtreVille, setFiltreVille] = useState("toutes");
  const [editContact, setEditContact] = useState(false);

  // ── Formulaire véhicule ──────────────────────────────────
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(VEHICULE_VIDE);
  const [photos, setPhotos]       = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null);

  // ── Contact ──────────────────────────────────────────────
  const [contactData, setContactData] = useState({
    telephone:       user.telephone || "",
    whatsapp:        user.telephone || "",
    email:           user.email     || "",
    adresse_parking: "",
  });
  const [savingContact, setSavingContact] = useState(false);

  // ── Charger le profil complet (whatsapp, adresse parking déjà enregistrés) ──
  useEffect(() => {
    const chargerProfil = async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/profile`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (res.ok) {
          setContactData({
            telephone:       data.telephone || "",
            whatsapp:        data.whatsapp  || data.telephone || "",
            email:           data.email     || "",
            adresse_parking: data.adresseParking || "",
          });
        }
      } catch (e) { console.error("Erreur chargement profil:", e); }
    };
    chargerProfil();
  }, []);

  // ── Sauvegarder les coordonnées publiques ─────────────────
  const sauvegarderContact = async () => {
    setSavingContact(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          telephone: contactData.telephone,
          whatsapp: contactData.whatsapp,
          adresseParking: contactData.adresse_parking,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        // Garde le localStorage synchronisé pour le reste du site
        const userLocal = getUser();
        localStorage.setItem("user", JSON.stringify({ ...userLocal, telephone: contactData.telephone }));
        setEditContact(false);
      } else {
        alert(data.message || "Erreur lors de la sauvegarde.");
      }
    } catch (e) {
      alert("Erreur serveur.");
    } finally {
      setSavingContact(false);
    }
  };

  // ── Chargement des véhicules ─────────────────────────────
  useEffect(() => {
    const charger = async () => {
      try {
        const res = await fetch(`${BASE_URL}/transports/mes-vehicules`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        setVehicules(Array.isArray(data) ? data : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    charger();
  }, []);

  // ── Upload photos Cloudinary ─────────────────────────────
  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const fd = new FormData();
        fd.append("file", file); fd.append("upload_preset", UPLOAD_PRESET);
        const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method: "POST", body: fd });
        const d = await r.json(); return d.secure_url;
      }));
      setPhotos(prev => [...prev, ...urls]);
    } catch { alert("Erreur upload."); }
    finally { setUploading(false); }
  };

  const toggleEquip = (e) => setForm(prev => ({
    ...prev,
    equipements: prev.equipements.includes(e)
      ? prev.equipements.filter(x => x !== e)
      : [...prev.equipements, e],
  }));

  // ── Sauvegarder véhicule ──────────────────────────────────
  const sauvegarderVehicule = async () => {
    if (!form.nom || !form.prix || !form.telephone) {
      alert("Nom, prix et téléphone sont obligatoires."); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        type: CATEGORIE_SERVICE, // ── catégorie de service, fixe pour ce dashboard
        prix:       parseInt(form.prix)      || 0,
        prixJournee: parseInt(form.prixJournee) || parseInt(form.prix) || 0,
        capacite:   parseInt(form.capacite)  || 4,
        annee:      parseInt(form.annee)     || null,
        images:     photos,
      };
      const url    = editId ? `${BASE_URL}/transports/${editId}` : `${BASE_URL}/transports`;
      const method = editId ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        if (editId) {
          setVehicules(prev => prev.map(v => v.id === editId ? data.transport : v));
        } else {
          setVehicules(prev => [data.transport, ...prev]);
        }
        setShowForm(false); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null);
      } else alert(data.message || "Erreur.");
    } catch { alert("Erreur serveur."); }
    finally { setSaving(false); }
  };

  const ouvrirEdit = (v) => {
    setEditId(v.id);
    setForm({
      nom: v.nom || "",
      // Repli sur v.type pour les anciens enregistrements créés avant la correction du bug
      typeVehicule: v.typeVehicule || v.type || "Berline",
      marque: v.marque || "",
      modele: v.modele || "", annee: v.annee || "", villeDepart: v.villeDepart || "Dakar",
      prix: v.prix || "", prixJournee: v.prixJournee || "", capacite: v.capacite || "",
      telephone: v.telephone || "", whatsapp: v.whatsapp || "",
      nomProprietaire: v.nomProprietaire || "", description: v.description || "",
      equipements: Array.isArray(v.equipements) ? v.equipements : [],
      disponible: v.disponible ?? true,
    });
    setPhotos(Array.isArray(v.images) ? v.images : []);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const supprimerVehicule = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try {
      await fetch(`${BASE_URL}/transports/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setVehicules(prev => prev.filter(v => v.id !== id));
    } catch (e) { console.error(e); }
  };

  // ── Filtres ──────────────────────────────────────────────
  const vehiculesFiltres = vehicules.filter(v => {
    const matchType  = filtreType  === "tous"   || v.typeVehicule === filtreType;
    const matchVille = filtreVille === "toutes" || v.villeDepart  === filtreVille;
    return matchType && matchVille;
  });

  const vehiculesDisponibles = vehicules.filter(v => v.disponible).length;

  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700,
    color: "#7A5A3A", textTransform: "uppercase",
    letterSpacing: "0.5px", marginBottom: 6,
  };
  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "11px 14px",
    border: "1.5px solid #E8DCC8", borderRadius: 10,
    fontFamily: "'Space Grotesk',sans-serif", fontSize: 14,
    outline: "none", color: "#1C1008", background: "white",
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF5EC", fontFamily: "'Space Grotesk',sans-serif", color: "#7A5A3A" }}>
      Chargement…
    </div>
  );

  return (
    <>
      <Navbar />
      <div style={{ fontFamily: "'Space Grotesk',sans-serif", background: "#FAF5EC", minHeight: "100vh", color: "#1C1008" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,700;0,9..144,800;1,9..144,400&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
          .onglet-btn { transition: all .2s; cursor: pointer; border: none; background: none; }
          .onglet-btn:hover { color: #C4441A !important; }
          .card-hover { transition: box-shadow .2s, transform .2s; }
          .card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(28,16,8,0.10) !important; }
          .btn-primary { background: #C4441A; transition: background .2s, transform .15s; border: none; cursor: pointer; }
          .btn-primary:hover { background: #A03614; transform: translateY(-1px); }
          .btn-whatsapp { background: #25D366; transition: background .2s; border: none; cursor: pointer; }
          .btn-whatsapp:hover { background: #1DA851; }
          .vehicule-card { transition: all .2s; }
          .vehicule-card:hover { box-shadow: 0 8px 24px rgba(28,16,8,0.10) !important; transform: translateY(-2px); }
          .pill { cursor: pointer; transition: all .15s; user-select: none; }
          .photo-zone { border: 2px dashed #E8DCC8; border-radius: 12px; padding: 28px; text-align: center; cursor: pointer; transition: border-color .2s; }
          .photo-zone:hover { border-color: #C4441A; }
          @media (max-width: 767px) {
            .stats-grid { grid-template-columns: 1fr 1fr !important; }
            .vehicules-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* ── HEADER ── */}
        <div style={{ background: "linear-gradient(135deg, #1C1008 0%, #2D1A08 100%)", padding: "36px 5%", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle,rgba(196,68,26,.07) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#C4441A,#E8834A)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "white", border: "3px solid rgba(255,255,255,0.2)", flexShrink: 0, overflow: "hidden" }}>
                  {localStorage.getItem("sunutour_photo")
                    ? <img src={localStorage.getItem("sunutour_photo")} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="profil" />
                    : (user.prenom?.[0] || "") + (user.nom?.[0] || "")
                  }
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#C4441A", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>Dashboard Loueur · JOJ 2026</div>
                  <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 800, color: "white", margin: 0 }}>
                    {`${user.prenom || ""} ${user.nom || ""}`.trim() || "Mon espace loueur"}
                  </h1>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                    {vehicules.length} véhicule{vehicules.length !== 1 ? "s" : ""} · {user.ville || "Dakar"}
                  </div>
                </div>
              </div>
              <button className="btn-primary" onClick={() => { setOnglet("vehicules"); setShowForm(true); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                style={{ color: "white", borderRadius: 10, padding: "12px 22px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 8px 20px rgba(196,68,26,0.35)" }}>
                + Ajouter un véhicule
              </button>
            </div>
          </div>
        </div>

        {/* ── ONGLETS ── */}
        <div style={{ background: "white", borderBottom: "1.5px solid #E8DCC8", padding: "0 5%", overflowX: "auto" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 4, minWidth: "max-content" }}>
            {ONGLETS.map(o => (
              <button key={o.id} className="onglet-btn" onClick={() => setOnglet(o.id)}
                style={{ padding: "16px 18px", fontSize: 13, fontWeight: 600, color: onglet === o.id ? "#C4441A" : "#7A5A3A", borderBottom: onglet === o.id ? "2px solid #C4441A" : "2px solid transparent", marginBottom: -1, whiteSpace: "nowrap" }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "36px 5% 60px" }}>

          {/* ══ VUE GÉNÉRALE ══ */}
          {onglet === "dashboard" && (
            <>
              {vehicules.length === 0 && (
                <div style={{ background: "rgba(196,132,26,0.06)", border: "1.5px solid rgba(196,132,26,0.25)", borderRadius: 12, padding: "20px 24px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#C4841A", marginBottom: 4 }}>Aucun véhicule enregistré</div>
                    <div style={{ fontSize: 13, color: "#7A5A3A" }}>Ajoutez votre premier véhicule pour être visible sur la plateforme.</div>
                  </div>
                  <button className="btn-primary" onClick={() => { setOnglet("vehicules"); setShowForm(true); }}
                    style={{ color: "white", borderRadius: 8, padding: "10px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>
                    Ajouter un véhicule →
                  </button>
                </div>
              )}

              <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
                {[
                  { label: "Total véhicules",   value: vehicules.length,          color: "#C4441A" },
                  { label: "Disponibles",        value: vehiculesDisponibles,      color: "#3D6B2E" },
                  { label: "Masqués",            value: vehicules.length - vehiculesDisponibles, color: "#C4841A" },
                  { label: "Note moyenne",       value: vehicules.length > 0 ? (vehicules.reduce((s,v) => s + (v.note||0), 0) / vehicules.length).toFixed(1) + "/5" : "—", color: "#C4441A" },
                ].map((s, i) => (
                  <div key={i} className="card-hover" style={{ background: "white", borderRadius: 14, padding: "22px", border: "1.5px solid #E8DCC8", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: "#7A5A3A", marginTop: 6, fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", overflow: "hidden" }}>
                  <div style={{ padding: "18px 24px", borderBottom: "1.5px solid #E8DCC8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes véhicules</h3>
                    <button onClick={() => setOnglet("vehicules")} style={{ fontSize: 12, color: "#C4441A", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>Voir tout →</button>
                  </div>
                  {vehicules.length === 0
                    ? <div style={{ padding: 32, textAlign: "center", color: "#7A5A3A", fontSize: 13 }}>Aucun véhicule pour l'instant</div>
                    : vehicules.slice(0, 4).map((v, i, arr) => (
                      <div key={v.id} style={{ padding: "14px 24px", borderBottom: i < arr.length - 1 ? "1px solid #F2E4C8" : "none", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 44, height: 36, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#F2E4C8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                          {v.images?.[0] ? <img src={v.images[0]} alt={v.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🚗"}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1008", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.nom || `${v.marque} ${v.modele}`}</div>
                          <div style={{ fontSize: 11, color: "#7A5A3A" }}>{v.typeVehicule || v.type} · {v.villeDepart}</div>
                        </div>
                        <div style={{ fontFamily: "'Fraunces',serif", fontSize: 14, fontWeight: 700, color: "#C4441A" }}>{formatPrix(v.prix)}<span style={{ fontSize: 11, fontWeight: 500 }}>/j</span></div>
                      </div>
                    ))
                  }
                </div>

                <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", overflow: "hidden" }}>
                  <div style={{ padding: "18px 24px", borderBottom: "1.5px solid #E8DCC8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: "#1C1008", margin: 0 }}>Locations en cours</h3>
                    <button onClick={() => setOnglet("locations")} style={{ fontSize: 12, color: "#C4441A", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>Voir tout →</button>
                  </div>
                  <div style={{ padding: 32, textAlign: "center", color: "#7A5A3A", fontSize: 13 }}>
                    Les demandes de location apparaîtront ici
                  </div>
                </div>
              </div>

              <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", padding: "20px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes coordonnées publiques</h3>
                  <button onClick={() => setOnglet("contact")} style={{ fontSize: 12, color: "#C4441A", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>Modifier →</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                  {[
                    { label: "WhatsApp",  value: contactData.whatsapp        || "Non renseigné" },
                    { label: "Téléphone", value: contactData.telephone       || "Non renseigné" },
                    { label: "Email",     value: contactData.email           || "Non renseigné" },
                    { label: "Parking",   value: contactData.adresse_parking || "Non renseigné" },
                  ].map((c, i) => (
                    <div key={i} style={{ background: "#FAF5EC", borderRadius: 10, padding: "14px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#7A5A3A", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{c.label}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1008", wordBreak: "break-all" }}>{c.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══ MES VÉHICULES ══ */}
          {onglet === "vehicules" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes véhicules</h2>
                  <p style={{ fontSize: 13, color: "#7A5A3A", margin: "4px 0 0" }}>{vehicules.length} véhicule{vehicules.length !== 1 ? "s" : ""} enregistré{vehicules.length !== 1 ? "s" : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <select value={filtreVille} onChange={e => setFiltreVille(e.target.value)}
                    style={{ padding: "9px 14px", border: "1.5px solid #E8DCC8", borderRadius: 8, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#1C1008", background: "white", cursor: "pointer", outline: "none" }}>
                    <option value="toutes">Toutes les villes</option>
                    {VILLES.map(v => <option key={v}>{v}</option>)}
                  </select>
                  <select value={filtreType} onChange={e => setFiltreType(e.target.value)}
                    style={{ padding: "9px 14px", border: "1.5px solid #E8DCC8", borderRadius: 8, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#1C1008", background: "white", cursor: "pointer", outline: "none" }}>
                    <option value="tous">Tous les types</option>
                    {TYPES_VEHICULE.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <button className="btn-primary" onClick={() => { setShowForm(!showForm); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                    style={{ color: "white", borderRadius: 10, padding: "10px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700 }}>
                    {showForm && !editId ? "Annuler" : "+ Ajouter"}
                  </button>
                </div>
              </div>

              {showForm && (
                <div style={{ background: "white", borderRadius: 16, border: "1.5px solid #E8DCC8", padding: "32px", marginBottom: 28, boxShadow: "0 4px 20px rgba(28,16,8,0.08)" }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 800, color: "#1C1008", marginBottom: 24 }}>
                    {editId ? "Modifier le véhicule" : "Nouveau véhicule"}
                  </h3>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Nom de l'annonce *</label>
                      <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Toyota Corolla 2022 climatisée" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Type de véhicule</label>
                      <select value={form.typeVehicule} onChange={e => setForm({ ...form, typeVehicule: e.target.value })} style={inputStyle}>
                        {TYPES_VEHICULE.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Marque</label>
                      <input value={form.marque} onChange={e => setForm({ ...form, marque: e.target.value })} placeholder="Ex: Toyota" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Modèle</label>
                      <input value={form.modele} onChange={e => setForm({ ...form, modele: e.target.value })} placeholder="Ex: Corolla" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Année</label>
                      <input type="number" value={form.annee} onChange={e => setForm({ ...form, annee: e.target.value })} placeholder="Ex: 2022" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Ville</label>
                      <select value={form.villeDepart} onChange={e => setForm({ ...form, villeDepart: e.target.value })} style={inputStyle}>
                        {VILLES.map(v => <option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Capacité (places)</label>
                      <input type="number" value={form.capacite} onChange={e => setForm({ ...form, capacite: e.target.value })} placeholder="Ex: 5" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Disponible</label>
                      <select value={form.disponible ? "oui" : "non"} onChange={e => setForm({ ...form, disponible: e.target.value === "oui" })} style={inputStyle}>
                        <option value="oui">Oui — visible</option>
                        <option value="non">Non — masqué</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Prix / jour (F CFA) *</label>
                      <input type="number" value={form.prix} onChange={e => setForm({ ...form, prix: e.target.value })} placeholder="Ex: 35000" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Nom du propriétaire</label>
                      <input value={form.nomProprietaire} onChange={e => setForm({ ...form, nomProprietaire: e.target.value })} placeholder="Ex: Mamadou Diop" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>Téléphone *</label>
                      <input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} placeholder="+221 77 000 00 00" style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>WhatsApp</label>
                      <input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="+221 77 000 00 00" style={inputStyle} />
                      <p style={{ fontSize: 11, color: "#B8A898", marginTop: 4 }}>Les clients cliqueront sur ce numéro pour vous contacter directement.</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Description</label>
                    <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="Décrivez le véhicule, ses options, conditions de location…"
                      rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Équipements</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {EQUIPEMENTS_LISTE.map(e => (
                        <span key={e} className="pill" onClick={() => toggleEquip(e)}
                          style={{ padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${form.equipements.includes(e) ? "#C4441A" : "#E8DCC8"}`, background: form.equipements.includes(e) ? "rgba(196,68,26,0.08)" : "white", color: form.equipements.includes(e) ? "#C4441A" : "#7A5A3A" }}>
                          {form.equipements.includes(e) ? "✓ " : ""}{e}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 24 }}>
                    <label style={labelStyle}>Photos du véhicule</label>
                    <div className="photo-zone" onClick={() => fileRef.current?.click()}>
                      <input ref={fileRef} type="file" multiple accept="image/*" onChange={e => uploadPhotos(Array.from(e.target.files))} style={{ display: "none" }} />
                      {uploading ? <p style={{ color: "#7A5A3A", margin: 0 }}>Chargement…</p> : <p style={{ color: "#7A5A3A", margin: 0, fontSize: 14, fontWeight: 600 }}>📷 Cliquez pour ajouter des photos</p>}
                    </div>
                    {photos.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
                        {photos.map((url, i) => (
                          <div key={i} style={{ position: "relative", width: 80, height: 80, borderRadius: 8, overflow: "hidden" }}>
                            <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button onClick={() => setPhotos(prev => prev.filter(p => p !== url))}
                              style={{ position: "absolute", top: 4, right: 4, background: "rgba(196,68,26,0.85)", border: "none", borderRadius: "50%", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white", fontSize: 12 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={() => { setShowForm(false); setForm(VEHICULE_VIDE); setPhotos([]); setEditId(null); }}
                      style={{ background: "white", border: "1.5px solid #E8DCC8", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, color: "#7A5A3A", cursor: "pointer" }}>
                      Annuler
                    </button>
                    <button className="btn-primary" onClick={sauvegarderVehicule} disabled={saving}
                      style={{ color: "white", borderRadius: 10, padding: "11px 28px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, opacity: saving ? 0.7 : 1 }}>
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                  </div>
                </div>
              )}

              {vehiculesFiltres.length === 0 && !showForm ? (
                <div style={{ textAlign: "center", padding: 60, color: "#7A5A3A" }}>
                  <p style={{ fontSize: 15, marginBottom: 16 }}>Aucun véhicule trouvé.</p>
                  <button className="btn-primary" onClick={() => setShowForm(true)} style={{ color: "white", borderRadius: 10, padding: "12px 24px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700 }}>
                    + Ajouter mon premier véhicule
                  </button>
                </div>
              ) : (
                <div className="vehicules-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
                  {vehiculesFiltres.map(v => (
                    <div key={v.id} className="vehicule-card" style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", overflow: "hidden", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>
                      <div style={{ height: 120, background: "linear-gradient(135deg,#1C1008,#2D1A08)", overflow: "hidden", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 48 }}>
                        {v.images?.[0] ? <img src={v.images[0]} alt={v.nom} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🚗"}
                        <span style={{ position: "absolute", top: 10, right: 10, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: v.disponible ? "rgba(61,107,46,0.85)" : "rgba(196,68,26,0.85)", color: "white" }}>
                          {v.disponible ? "Disponible" : "Masqué"}
                        </span>
                        <span style={{ position: "absolute", top: 10, left: 10, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "rgba(255,255,255,0.15)", color: "white" }}>
                          {v.typeVehicule || v.type}
                        </span>
                      </div>
                      <div style={{ padding: "16px" }}>
                        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 15, fontWeight: 800, color: "#1C1008", margin: "0 0 4px" }}>{v.nom || `${v.marque} ${v.modele}`}</h3>
                        <div style={{ fontSize: 12, color: "#7A5A3A", marginBottom: 6 }}>{v.annee} · {v.capacite} places · {v.villeDepart}</div>
                        {v.description && <p style={{ fontSize: 12, color: "#7A5A3A", lineHeight: 1.5, margin: "0 0 10px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{v.description}</p>}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid #F2E4C8" }}>
                          <div>
                            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 17, fontWeight: 800, color: "#C4441A" }}>{formatPrix(v.prix)}</div>
                            <div style={{ fontSize: 11, color: "#7A5A3A" }}>par jour</div>
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => ouvrirEdit(v)} style={{ background: "rgba(196,68,26,0.08)", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, color: "#C4441A", cursor: "pointer" }}>✏️</button>
                            <button onClick={() => supprimerVehicule(v.id)} style={{ background: "rgba(196,68,26,0.06)", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, color: "#C4441A", cursor: "pointer" }}>🗑️</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ══ LOCATIONS EN COURS ══ */}
          {onglet === "locations" && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🚗</div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", marginBottom: 12 }}>Locations en cours</h2>
              <p style={{ fontSize: 14, color: "#7A5A3A", maxWidth: 400, margin: "0 auto" }}>
                Les clients intéressés vous contactent directement via WhatsApp. Les demandes de location apparaîtront ici prochainement.
              </p>
            </div>
          )}

          {/* ══ HISTORIQUE & REVENUS ══ */}
          {onglet === "historique" && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", marginBottom: 12 }}>Historique & Revenus</h2>
              <p style={{ fontSize: 14, color: "#7A5A3A", maxWidth: 400, margin: "0 auto" }}>
                L'historique de vos locations et le suivi de vos revenus seront disponibles prochainement.
              </p>
            </div>
          )}

          {/* ══ NOTES & AVIS ══ */}
          {onglet === "avis" && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⭐</div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", marginBottom: 12 }}>Notes & Avis</h2>
              <p style={{ fontSize: 14, color: "#7A5A3A", maxWidth: 400, margin: "0 auto" }}>
                Les avis clients apparaîtront ici dès que des clients auront loué vos véhicules et laissé une note.
              </p>
            </div>
          )}

          {/* ══ CONTACT & PARKING ══ */}
          {onglet === "contact" && (
            <>
              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 800, color: "#1C1008", margin: 0 }}>Contact & Parking</h2>
                <p style={{ fontSize: 13, color: "#7A5A3A", margin: "4px 0 0" }}>Ces informations sont visibles par les clients sur la page de location</p>
              </div>

              <div style={{ background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8", padding: "32px", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 800, color: "#1C1008", margin: 0 }}>Mes coordonnées publiques</h3>
                  {!editContact && (
                    <button className="btn-primary" onClick={() => setEditContact(true)}
                      style={{ color: "white", borderRadius: 10, padding: "10px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 700 }}>
                      ✏️ Modifier
                    </button>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
                  {[
                    { key: "whatsapp",        label: "Numéro WhatsApp",    placeholder: "+221 77 000 00 00" },
                    { key: "telephone",       label: "Téléphone",          placeholder: "+221 77 000 00 00" },
                    { key: "email",           label: "Email",              placeholder: "contact@email.com" },
                    { key: "adresse_parking", label: "Adresse du parking", placeholder: "Ex: Parking Liberté 6, Dakar" },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={labelStyle}>{f.label}</label>
                      <input
                        value={contactData[f.key]}
                        onChange={e => setContactData({ ...contactData, [f.key]: e.target.value })}
                        disabled={!editContact}
                        placeholder={f.placeholder}
                        style={{ ...inputStyle, background: editContact ? "white" : "#FAF5EC", color: editContact ? "#1C1008" : "#7A5A3A", cursor: editContact ? "text" : "default" }}
                      />
                    </div>
                  ))}
                </div>

                <div style={{ background: "#FAF5EC", borderRadius: 12, padding: "20px", marginBottom: editContact ? 24 : 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#7A5A3A", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 12 }}>Aperçu — ce que verra le client</div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {contactData.whatsapp && (
                      <a href={`https://wa.me/${contactData.whatsapp.replace(/[\s+]/g, "")}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                        <button className="btn-whatsapp" style={{ color: "white", borderRadius: 10, padding: "12px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700 }}>
                          💬 Contacter sur WhatsApp
                        </button>
                      </a>
                    )}
                    {contactData.telephone && (
                      <a href={`tel:${contactData.telephone}`} style={{ textDecoration: "none" }}>
                        <button style={{ background: "white", border: "1.5px solid #E8DCC8", borderRadius: 10, padding: "12px 20px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700, color: "#1C1008", cursor: "pointer" }}>
                          📞 Appeler
                        </button>
                      </a>
                    )}
                  </div>
                </div>

                {editContact && (
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => setEditContact(false)}
                      style={{ background: "white", border: "1.5px solid #E8DCC8", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 600, color: "#7A5A3A", cursor: "pointer" }}>
                      Annuler
                    </button>
                    <button className="btn-primary" onClick={sauvegarderContact} disabled={savingContact}
                      style={{ flex: 1, color: "white", borderRadius: 10, padding: "12px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: savingContact ? 0.7 : 1 }}>
                      {savingContact ? "Enregistrement…" : "✓ Enregistrer"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
}